import React, { useEffect, useRef, useState } from 'react';
import { adminGetReviews, adminCreateReview, adminUpdateReview, adminDeleteReview } from '../../utils/api';

const BASE_URL = '';
const EMPTY = { name: '', text: '', rating: 5 };

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const avatarRef = useRef();
  const photosRef = useRef();

  const load = () => {
    setLoading(true);
    adminGetReviews()
      .then(r => setReviews(r.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setAvatarFile(null); setAvatarPreview(null);
    setPhotoFiles([]); setPhotoPreviews([]);
    setError('');
    setModal(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, text: r.text, rating: r.rating });
    setAvatarFile(null);
    setAvatarPreview(r.avatar ? BASE_URL + r.avatar : null);
    setPhotoFiles([]);
    setPhotoPreviews((r.photos || []).map(p => BASE_URL + p));
    setError('');
    setModal(true);
  };

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleAvatarChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setAvatarFile(f);
    setAvatarPreview(URL.createObjectURL(f));
  };

  const handlePhotosChange = (e) => {
    const files = Array.from(e.target.files);
    setPhotoFiles(files);
    setPhotoPreviews(files.map(f => URL.createObjectURL(f)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('text', form.text);
      fd.append('rating', form.rating);
      if (avatarFile) fd.append('avatar', avatarFile);
      photoFiles.forEach(f => fd.append('photos', f));

      if (editing) {
        await adminUpdateReview(editing.id, fd);
      } else {
        await adminCreateReview(fd);
      }
      setModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка збереження');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Видалити відгук?')) return;
    await adminDeleteReview(id);
    load();
  };

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <div>
          <h2>⭐ Відгуки клієнтів</h2>
          <p className="admin-section-desc">Управляйте відгуками — додавайте фото автора та зображення до відгуку</p>
        </div>
        <button className="admin-btn admin-btn--primary" onClick={openCreate}>+ Додати відгук</button>
      </div>

      {loading ? (
        <div className="admin-loading">Завантаження...</div>
      ) : reviews.length === 0 ? (
        <div className="admin-card">
          <p className="admin-empty">Відгуків ще немає. Додайте перший!</p>
        </div>
      ) : (
        <div className="admin-reviews-grid">
          {reviews.map(r => (
            <div key={r.id} className="admin-review-card">
              <div className="admin-review-header">
                <div className="admin-review-author">
                  {r.avatar ? (
                    <img src={BASE_URL + r.avatar} alt={r.name} className="admin-review-avatar" />
                  ) : (
                    <div className="admin-review-avatar-placeholder">{r.name.charAt(0).toUpperCase()}</div>
                  )}
                  <div>
                    <strong>{r.name}</strong>
                    <div className="stars-sm">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</div>
                  </div>
                </div>
                <div className="admin-review-actions">
                  <button className="admin-btn admin-btn--sm" onClick={() => openEdit(r)}>✏️</button>
                  <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => handleDelete(r.id)}>🗑</button>
                </div>
              </div>
              <p className="admin-review-text">{r.text}</p>
              {(r.photos || []).length > 0 && (
                <div className="admin-review-photos">
                  {(r.photos || []).map((p, i) => (
                    <img key={i} src={BASE_URL + p} alt="фото відгуку" className="admin-review-photo-thumb" />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="admin-modal-overlay" onClick={() => setModal(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{editing ? 'Редагувати відгук' : 'Новий відгук'}</h3>
              <button className="admin-modal-close" onClick={() => setModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit} className="admin-form" encType="multipart/form-data">
              {error && <div className="admin-error">{error}</div>}

              <div className="admin-form-group">
                <label>Ім'я автора *</label>
                <input name="name" value={form.name} onChange={handleChange} required />
              </div>

              <div className="admin-form-group">
                <label>Рейтинг *</label>
                <select name="rating" value={form.rating} onChange={handleChange}>
                  {[5,4,3,2,1].map(n => <option key={n} value={n}>{n} зірки</option>)}
                </select>
              </div>

              <div className="admin-form-group">
                <label>Текст відгуку *</label>
                <textarea name="text" rows={4} value={form.text} onChange={handleChange} required />
              </div>

              {/* Avatar upload */}
              <div className="admin-form-group">
                <label>Фото автора</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  {avatarPreview && (
                    <img src={avatarPreview} alt="preview" style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border)' }} />
                  )}
                  <button type="button" className="admin-btn" onClick={() => avatarRef.current.click()}>
                    📷 Вибрати фото
                  </button>
                  <input ref={avatarRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
                </div>
              </div>

              {/* Photos upload */}
              <div className="admin-form-group">
                <label>Фото у відгуку (до 5)</label>
                <div>
                  <button type="button" className="admin-btn" onClick={() => photosRef.current.click()}>
                    🖼️ Вибрати фото
                  </button>
                  <input ref={photosRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handlePhotosChange} />
                  {photoPreviews.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                      {photoPreviews.map((p, i) => (
                        <img key={i} src={p} alt="" style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--border)' }} />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="admin-modal-footer">
                <button type="button" className="admin-btn" onClick={() => setModal(false)}>Скасувати</button>
                <button type="submit" className="admin-btn admin-btn--primary" disabled={submitting}>
                  {submitting ? 'Збереження...' : (editing ? '✓ Зберегти' : '+ Створити')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
